// Package-shaped, id-keyed GitHub sync (see docs plan "Design Packages" §
// Git). Every write is one atomic commit for the whole package directory —
// never a per-file commit — via the Git Data API (blobs/trees/commits).
// A package's stable identity is manifest.id, never its directory path; every
// function here is keyed by id and resolves the actual storage path from the
// current manifest listing (falling back to the local cache, then to a fresh
// slug for a brand-new package).

import * as github from './api.js'
import {
    savePackage as cacheSavePackage,
    loadPackage as cacheLoadPackage,
    listPackages as cacheListPackages,
    deletePackage as cacheDeletePackage,
} from '../cache/adapter.js'
import { DesignPackage } from '../package/DesignPackage.js'
import { slugify } from '../package/slug.js'
import { base64ToText } from '../package/base64.js'

export { testConnection, getFileHistory } from './api.js'

function rootFolder(config) {
    return (config.folder || 'designs').replace(/\/$/, '')
}

function manifestToEntry(manifest, path) {
    return {
        id: manifest.id,
        path,
        name: manifest.name,
        description: manifest.description,
        preview: manifest.preview,
    }
}

function decode({ content, encoding }) {
    return encoding === 'base64' ? base64ToText(content) : content
}

/** One recursive tree call + one blob fetch per manifest.json — never opens design.json just to list. */
async function fetchRemoteManifests(config) {
    const headSha = await github.getRef(config)
    const commit = await github.getCommit(config, headSha)
    const tree = await github.getTree(config, commit.tree.sha, true)
    const root = rootFolder(config)
    const manifestBlobs = tree.tree.filter(e =>
        e.type === 'blob' && e.path.startsWith(`${root}/`) && e.path.endsWith('/manifest.json')
    )
    const entries = []
    for (const item of manifestBlobs) {
        let manifest
        try {
            manifest = JSON.parse(decode(await github.getBlob(config, item.sha)))
        } catch {
            continue
        }
        entries.push(manifestToEntry(manifest, item.path.slice(0, -'/manifest.json'.length)))
    }
    return { headSha, entries }
}

/** Resolves a package id to its current storage directory (see module doc). */
async function resolvePath(config, id, fallbackName) {
    if (navigator.onLine) {
        try {
            const { entries } = await fetchRemoteManifests(config)
            const found = entries.find(e => e.id === id)
            if (found) return found.path
        } catch {
            // offline / misconfigured / not yet pushed — fall through
        }
    }
    const localRows = await cacheListPackages()
    const localRow = localRows.find(r => r.packageId === id)
    if (localRow) return localRow.packagePath
    return `${rootFolder(config)}/${slugify(fallbackName || id)}`
}

async function saveLocalPackage(id, path, fileList, localModified = false) {
    await cacheSavePackage(id, path, fileList.map(f => ({
        filePath: f.path, content: f.content, encoding: f.encoding, mime: f.mime ?? null, localModified,
    })))
}

/** DesignPackage reconstructed from this app's own local cache, or null. */
export async function loadCachedPackage(id) {
    const rows = await cacheLoadPackage(id)
    if (!rows || rows.length === 0) return null
    return DesignPackage.fromFileList(rows.map(r => ({ path: r.filePath, content: r.content, encoding: r.encoding, mime: r.mime })))
}

// ── List ──────────────────────────────────────────────────────────────────────
// Always loads local cache first, then tries GitHub and merges by id.
export async function listPackages(config) {
    const localRows = await cacheListPackages()
    const localEntries = localRows.map(r => {
        let manifest = {}
        try { manifest = JSON.parse(decode(r)) } catch { /* ignore */ }
        return { ...manifestToEntry(manifest, r.packagePath), _local: true, _localModified: r.localModified }
    })

    if (!navigator.onLine) return localEntries

    try {
        const { entries: remoteEntries } = await fetchRemoteManifests(config)
        const localById = Object.fromEntries(localEntries.map(e => [e.id, e]))
        const remoteIds = new Set(remoteEntries.map(e => e.id))
        const merged = remoteEntries.map(e => ({
            ...e,
            _local: !!localById[e.id],
            _localModified: localById[e.id]?._localModified ?? false,
        }))
        const localOnly = localEntries.filter(e => !remoteIds.has(e.id))
        return [...merged, ...localOnly]
    } catch {
        return localEntries
    }
}

// ── Push ──────────────────────────────────────────────────────────────────────
// One commit for the whole package directory — added/removed/renamed files
// all handled correctly, since the package's subtree in the new commit is
// always exactly `pkg.toFileList()`, never a diff against the previous version.
export async function pushPackage(config, pkg, message) {
    const id = pkg.manifest.id;
    const path = await resolvePath(config, id, pkg.manifest.name);
    const fileList = pkg.toFileList();

    if (!navigator.onLine) {
        await saveLocalPackage(id, path, fileList, true);
        return { _local: true, path };
    }

    try {
        const headSha = await github.getRef(config);
        const commit = await github.getCommit(config, headSha);
        const fullTree = await github.getTree(config, commit.tree.sha, true);

        const prefix = `${path}/`;
        const keep = fullTree.tree
            .filter(e => e.type === 'blob' && !e.path.startsWith(prefix))
            .map(e => ({ path: e.path, sha: e.sha }));

        const fresh = [];
        for (const f of fileList) {
            const sha = await github.createBlob(config, f.content, f.encoding === 'base64' ? 'base64' : 'utf-8');
            fresh.push({ path: prefix + f.path, sha });
        }

        const newTreeSha = await github.createTree(config, null, [...keep, ...fresh]);
        const commitSha = await github.createCommit(config, message, newTreeSha, headSha);
        await github.updateRef(config, commitSha);

        await saveLocalPackage(id, path, fileList, false);
        return { commitSha, path };
    } catch (e) {
        await saveLocalPackage(id, path, fileList, true);
        throw e;
    }
}

// ── Restore ───────────────────────────────────────────────────────────────────
// Reconstructs a package as it existed at a given commit ref.
export async function getPackageAtRef(config, id, ref) {
    if (!navigator.onLine) {
        const cached = await loadCachedPackage(id);
        if (!cached) throw new Error('Offline — no local cache available for this package');
        return cached;
    }
    try {
        const path = await resolvePath(config, id);
        const commit = await github.getCommit(config, ref);
        const tree = await github.getTree(config, commit.tree.sha, true);
        const prefix = `${path}/`;
        const entries = [];
        for (const item of tree.tree) {
            if (item.type !== 'blob' || !item.path.startsWith(prefix)) continue;
            const blob = await github.getBlob(config, item.sha);
            entries.push({ path: item.path.slice(prefix.length), content: blob.content, encoding: blob.encoding, mime: null });
        }
        return DesignPackage.fromFileList(entries);
    } catch (e) {
        const cached = await loadCachedPackage(id);
        if (cached) return cached;
        throw new Error('GitHub unavailable and no local cache for this package');
    }
}

/** Commit history for the package's directory (unchanged mechanism — commits?path=<dir> already works for a directory). */
export async function getPackageHistory(config, id) {
    const path = await resolvePath(config, id);
    return github.getFileHistory(config, path);
}

// ── Delete ────────────────────────────────────────────────────────────────────
export async function deleteRemotePackage(config, id) {
    const path = await resolvePath(config, id);
    const headSha = await github.getRef(config);
    const commit = await github.getCommit(config, headSha);
    const fullTree = await github.getTree(config, commit.tree.sha, true);
    const prefix = `${path}/`;
    const keep = fullTree.tree
        .filter(e => e.type === 'blob' && !e.path.startsWith(prefix))
        .map(e => ({ path: e.path, sha: e.sha }));
    const newTreeSha = await github.createTree(config, null, keep);
    const commitSha = await github.createCommit(config, `Delete ${path}`, newTreeSha, headSha);
    await github.updateRef(config, commitSha);
    await cacheDeletePackage(id);
}

export async function deleteLocalPackage(id) {
    await cacheDeletePackage(id);
}

export async function listLocalPackages() {
    return cacheListPackages();
}
